/**
 * "Crumple & fly" animation for the export dialog.
 *
 * When an async export is enqueued, the dialog shrinks into a small ball and flies towards the
 * exports button in the app header, then fades out — the same metaphor browsers use for downloads.
 * It tells the user, without words, that the queued export now lives in that button (where they'll
 * see it processing and can later download it).
 */

/** Data attribute marking the header exports button: the landing target of the fly animation. */
export const EXPORTS_ANCHOR_ATTR = 'data-exports-anchor'

const DURATION_MS = 700

/** Respect the user's OS-level "reduce motion" setting. */
export const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Quick scale pulse on the target so the landing reads as "it arrived here". */
const pulseTarget = (target: HTMLElement) => {
    if (typeof target.animate !== 'function') return
    target.animate([{transform: 'scale(1)'}, {transform: 'scale(1.3)'}, {transform: 'scale(1)'}], {
        duration: 350,
        easing: 'ease-out'
    })
}

/**
 * Animate `source` (the dialog element) into the header exports button.
 * Resolves `true` once the dialog has flown out (the caller should then close/unmount it), or
 * `false` without animating when motion is reduced, the Web Animations API is unavailable, or
 * either element is missing (the caller should then leave the dialog as-is).
 */
export const flyToExportsToggle = async (source: HTMLElement | null): Promise<boolean> => {
    if (!source || prefersReducedMotion() || typeof source.animate !== 'function') return false

    const target = document.querySelector<HTMLElement>(`[${EXPORTS_ANCHOR_ATTR}]`)
    if (!target) return false

    const sourceRect = source.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    if (!sourceRect.width || !sourceRect.height) return false

    // The dialog is centred via `position: fixed; top/left: 50%; transform: translate(-50%, -50%)`.
    // We drive the whole transform ourselves from a top-left origin, so the layout reference point
    // (the element's untransformed top-left) sits at the viewport centre.
    const layoutX = window.innerWidth / 2
    const layoutY = window.innerHeight / 2

    const sourceCenter = {x: sourceRect.left + sourceRect.width / 2, y: sourceRect.top + sourceRect.height / 2}
    const targetCenter = {x: targetRect.left + targetRect.width / 2, y: targetRect.top + targetRect.height / 2}

    // Shrink to roughly the button's size — a small ball — clamped to a sensible range.
    const endScale = Math.min(Math.max(targetRect.width / sourceRect.width, 0.05), 0.2)

    // Translate needed to place the (scaled) element's centre at a given viewport point.
    const translateFor = (centerX: number, centerY: number, scale: number) => ({
        x: centerX - layoutX - (scale * sourceRect.width) / 2,
        y: centerY - layoutY - (scale * sourceRect.height) / 2
    })

    const start = translateFor(sourceCenter.x, sourceCenter.y, 1)
    const midScale = 0.6
    const mid = translateFor(
        sourceCenter.x + (targetCenter.x - sourceCenter.x) * 0.5,
        // Arc slightly upward for a livelier, more physical path.
        sourceCenter.y + (targetCenter.y - sourceCenter.y) * 0.5 - 64,
        midScale
    )
    const end = translateFor(targetCenter.x, targetCenter.y, endScale)

    source.style.transformOrigin = 'top left'
    source.style.willChange = 'transform, opacity'
    source.style.pointerEvents = 'none'

    const animation = source.animate(
        [
            {transform: `translate(${start.x}px, ${start.y}px) scale(1)`, opacity: 1, borderRadius: '12px', offset: 0},
            {
                transform: `translate(${mid.x}px, ${mid.y}px) scale(${midScale}) rotate(-3deg)`,
                opacity: 1,
                borderRadius: '24px',
                offset: 0.5
            },
            {
                transform: `translate(${end.x}px, ${end.y}px) scale(${endScale}) rotate(-10deg)`,
                opacity: 0.1,
                borderRadius: '50%',
                offset: 1
            }
        ],
        {duration: DURATION_MS, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'forwards'}
    )

    // Fade the backdrop out in lockstep, so the app behind reappears as the dialog leaves.
    const backdrop = document.querySelector<HTMLElement>('[data-backdrop]')
    backdrop?.animate([{opacity: 1}, {opacity: 0}], {duration: DURATION_MS, easing: 'ease-out', fill: 'forwards'})

    try {
        await animation.finished
    } catch {
        // Cancelled (e.g. the element unmounted mid-flight) — nothing to clean up.
        return false
    }

    pulseTarget(target)
    return true
}
